import React from 'react';

import RDSHeading from './components/RDSHeading/RDSHeading';
import BudgetGroup from './Budget/BudgetGroup/BudgetGroup';
import BudgetItem from './Budget/BudgetItem/BudgetItem';

import styles from './BudgetCalculator.module.css';
import BudgetDifference from './Budget/BudgetDifference/BudgetDifference';
import DefaultBudgetCTA from './Budget/DefaultBudgetCTA/DefaultBudgetCTA';
import BudgetSummary from './Budget/BudgetSummary';
import {
  BudgetItemColors,
  BudgetItemLabels,
  BudgetItemToolTipText,
} from './constants';
import useBudgetCalculator from './useBudgetCalculator';
import { BudgetExpenseId } from './types';
import RDSText from './components/RDSText';

type BudgetCalculatorProps = {
  callToActionHeader?: string;
  callToActionText?: string;
  callToActionButtonText?: string;
  callToActionButtonLink?: string;
  subText?: string;
};

const BudgetCalculator = (props: BudgetCalculatorProps) => {
  const {
    callToActionHeader,
    callToActionText,
    callToActionButtonText,
    callToActionButtonLink,
    subText = "Enter your income and the calculator will show the national averages for most budget categories as a starting point. A few of these are recommendations (like giving). Most just reflect average spending (like debt). Don't have debt? Yay! Move that money to your current money goal.",
  } = props || {};
  const {
    budgetValues,
    updateBudgetItem,
    getExpenses,
    getDifference,
    getChartData,
  } = useBudgetCalculator();

  const showCta = callToActionHeader && callToActionText;

  return (
    <div className={styles.BudgetCalculator}>
      <div className={styles.Container}>
        <RDSHeading level="1" size="large" className={styles.Title}>
          Budget Calculator
          <RDSText size="bodySmall">{subText}</RDSText>
        </RDSHeading>

        <div className={styles.BudgetItemsContainer}>
          <BudgetGroup headingText="Income" className={styles.IncomeGroup}>
            <BudgetItem
              label={BudgetItemLabels['monthlyIncome']}
              value={budgetValues.income.monthlyIncome}
              onBlur={updateBudgetItem('income', 'monthlyIncome')}
              color={BudgetItemColors['monthlyIncome']}
              tooltipText={BudgetItemToolTipText['monthlyIncome']}
            />
          </BudgetGroup>
          <BudgetGroup headingText="Expenses" className={styles.ExpenseGroup}>
            {Object.entries(budgetValues.expenses).map(([key, value]) => (
              <BudgetItem
                key={key}
                label={BudgetItemLabels[key as BudgetExpenseId]}
                value={value}
                onBlur={updateBudgetItem('expenses', key)}
                color={BudgetItemColors[key as BudgetExpenseId]}
                tooltipText={BudgetItemToolTipText[key as BudgetExpenseId]}
              />
            ))}
          </BudgetGroup>
          <BudgetDifference difference={getDifference()} />
        </div>
        <div className={styles.BudgetSummaryContainer}>
          <BudgetSummary totalAmount={getExpenses()} data={getChartData()} />
        </div>
        {showCta && (
          <div className={styles.BudgetCTAContainer}>
            <DefaultBudgetCTA
              header={callToActionHeader}
              text={callToActionText}
              buttonText={callToActionButtonText}
              buttonLink={callToActionButtonLink}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetCalculator;
